import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { signInAsAdmin, useSession } from '../lib/session';

/**
 * There is no admin console (D37). This is one screen, and the only thing a
 * session from it unlocks is a reversible show/hide toggle on photo detail and
 * the Hidden filter in the gallery (D40).
 *
 * Visually distinct from the guest login on purpose — nobody should ever be
 * confused about which of the two they are looking at. No self-service reset:
 * recovery is an AWS console action.
 */
export default function AdminLogin() {
  const session = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  if (session?.isAdmin) return <Navigate to="/gallery" replace />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(false);
    try {
      await signInAsAdmin(email, password);
      navigate('/gallery', { replace: true });
    } catch {
      setError(true);
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100dvh', display: 'grid', placeItems: 'center', padding: 24,
        background: 'var(--color-neutral-900)', color: 'var(--color-neutral-100)',
      }}
    >
      <form onSubmit={submit} style={{ width: 'min(380px, 100%)' }}>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 13, letterSpacing: '0.18em', textTransform: 'uppercase', opacity: 0.6 }}>
          Organisers
        </div>
        <h2 style={{ margin: '6px 0 20px', color: 'inherit' }}>Sign in</h2>

        <div className="field" style={{ marginBottom: 12 }}>
          <label htmlFor="admin-email" style={{ color: 'inherit', opacity: 0.7 }}>Email</label>
          <input
            id="admin-email" className="input" type="email" autoComplete="username"
            style={{ minHeight: 48, background: 'color-mix(in srgb, #f9f4ed 10%, transparent)', color: 'inherit', borderColor: 'transparent' }}
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="admin-password" style={{ color: 'inherit', opacity: 0.7 }}>Password</label>
          <input
            id="admin-password" className="input" type="password" autoComplete="current-password"
            style={{ minHeight: 48, background: 'color-mix(in srgb, #f9f4ed 10%, transparent)', color: 'inherit', borderColor: 'transparent' }}
            value={password} onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {error && <p style={{ fontSize: 13, color: 'var(--color-accent-300)', marginTop: 12 }} role="alert">That didn't match. Try again.</p>}

        <button type="submit" className="btn btn-primary btn-block" style={{ minHeight: 50, marginTop: 20 }} disabled={busy || !email || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <p style={{ fontSize: 11, opacity: 0.5, marginTop: 18, textAlign: 'center' }}>
          Guests use the ordinary sign-in. This one only adds hide and unhide.
        </p>
      </form>
    </div>
  );
}
